import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState } from '../../utils/listPageState'
import { renderDateTime } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'

const DEFAULT_SEARCH_SELECT = {
    searchStatus: 0,
    searchPopupStatus: '',
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
    })
}

const buildListSearch = ({ curPage, searchQuery }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
    })
}

const formatMedalQuantity = (quantity) => {
    const normalizedQuantity = String(quantity ?? '').trim()

    return normalizedQuantity ? formatNumber(quantity) : '-'
}

const QUANTITY_CELL_SX = {
    width: 150,
    minWidth: 150,
    pr: 8,
    whiteSpace: 'nowrap',
}

const REGISTERED_AT_CELL_SX = {
    width: 210,
    minWidth: 210,
    pl: 8,
    whiteSpace: 'nowrap',
}

const MEDAL_ORDER_TABLE_COLUMNS = [
    { width: 70 },
    { width: 180 },
    { width: 260 },
    { width: 250 },
    { width: 150 },
    { width: 210 },
    { width: 120 },
]

const MEDAL_ORDER_TABLE_MIN_WIDTH = MEDAL_ORDER_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

export default function MedalOrders() {
    const location = useLocation()
    const history = useHistory()
    const deleteInFlightRef = useRef(new Set())
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(null)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')

    const isBusy = Boolean(actionLoading)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/medal/getProductOrderList', {
                page: curPage,
                offset: offset,
                searchText: searchQuery,
                searchSelect: searchSelect
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '메달 견적서 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '메달 견적서 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (isBusy) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
        }
    }

    const resetFilters = () => {
        if (isBusy) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const hasActiveFilters = Boolean((searchQuery || '').trim())

    // 견적서 목록의 조회 실패와 검색 결과 없음을 테이블 영역에서 구분한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="메달 견적서 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading || isBusy}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 메달 견적서가 없습니다."
                    description="문의자명 검색어를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isBusy} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="접수된 메달 견적서 문의가 없습니다."
                description="메달 견적서 문의가 접수되면 이 목록에 표시됩니다."
            />
        )
    }

    const handleDelete = async (id) => {
        if (deleteInFlightRef.current.has(id)) {
            return
        }

        try {
            deleteInFlightRef.current.add(id)
            setActionLoading({ type: 'delete', id })

            const { data } = await API.post('/api/v1/admin/medal/deleteProductOrder', { medalOrderId: id })

            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('메달 주문을 삭제했습니다.')

                if (params.length === 1 && curPage > 1) {
                    setCurPage((prev) => prev - 1)
                } else {
                    await getData(false)
                }
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('메달 주문을 삭제하지 못했습니다.')
        } finally {
            deleteInFlightRef.current.delete(id)
            setActionLoading(null)
        }
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Grid item xs={12}>
                    <Spiner/>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>메달 견적서 리스트</Title>
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isBusy} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="문의자명"
                                type="search"
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={isBusy}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={isBusy}><SearchIcon /></IconButton>
                        </ListFilterForm>

                    </ListFilterBar>

                    <ListTableContainer>
                        <Table size="small" sx={{ minWidth: MEDAL_ORDER_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                            <colgroup>
                                {MEDAL_ORDER_TABLE_COLUMNS.map((column, index) => (
                                    <col key={index} style={{ width: column.width }} />
                                ))}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>문의자명</TableCell>
                                    <TableCell>제목</TableCell>
                                    <TableCell>상품</TableCell>
                                    <TableCell align="right" sx={QUANTITY_CELL_SX}>수량</TableCell>
                                    <TableCell sx={REGISTERED_AT_CELL_SX}>등록일</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {params.length > 0 ? (
                                    params.map((row) => (
                                        <TableRow key={row.medalOrderId} hover>
                                            <TableCell>{row.rownum}</TableCell>
                                            <TableCell>{row.username || '-'}</TableCell>
                                            <TableCell className="listTableLimitText">{row.title || '-'}</TableCell>
                                            <TableCell>
                                                <Box sx={{ fontWeight: 700 }}>
                                                    {row.productTitle || '-'}
                                                </Box>
                                                <Box sx={{ color: 'text.secondary' }}>
                                                    {row.diameterStr || '-'}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right" sx={QUANTITY_CELL_SX}>{formatMedalQuantity(row.quantity)}</TableCell>
                                            <TableCell sx={REGISTERED_AT_CELL_SX}>{renderDateTime(row.regTime)}</TableCell>
                                            <TableCell align="right">
                                                <ListActionGroup disabled={isBusy}>
                                                    <EditIcon path={'medalOrder'} id={row.medalOrderId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect}/>
                                                    <DeleteAlertDialog handleDelete={handleDelete} id={row.medalOrderId}/>
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
